import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCompleteAccount } from './useCompleteAccount';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * A guest picks an email and a password. Everything they already have stays
 * as it is; from here on they can also sign in the usual way.
 */
export const CompleteAccountDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('auth');
  const { complete, error, isSaving, reset } = useCompleteAccount();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setEmail('');
      setPassword('');
      reset();
    }
  }, [open, reset]);

  const submit = async () => {
    if (await complete(email, password)) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('guest.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('guest.hint')}</DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('guest.email')}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
          autoFocus
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('guest.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          helperText={t('guest.passwordHint')}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('guest.cancel')}
        </Button>
        <Button onClick={submit} variant="contained" disabled={isSaving || !email || !password}>
          {t('guest.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
